import API from './API.ts'

function serializeInvoiceCart (charges) {
  if (charges === null) {
    return null
  }

  return charges.map((charge) => {
    const item = Object.assign({}, charge)
    item.delivery_date = charge.delivery_date ? charge.delivery_date.format('YYYY-MM-DD') : null
    item.position_id = charge.position ? charge.position.id : null
    item.product_id = charge.product.id
    item.position = null
    item.product = null
    return item
  })
}

export default {
  //
  // GET
  //
  getPaymentTerms () {
    return API.httpClient.get('payment-terms')
  },
  calculatePaymentTermSchedules ({ amount, bookingId, bookingGroupId, charges = null }) {
    return API.httpClient.post('payment-terms/calculate-schedules', {
      amount,
      booking_id: bookingId,
      booking_group_id: bookingGroupId,
      items: serializeInvoiceCart(charges) || []
    })
  },
  createPaymentTerm (paymentTerm) {
    return API.httpClient.post('payment-terms', paymentTerm)
  },
  updatePaymentTerm (paymentTermId, paymentTerm) {
    return API.httpClient.post('payment-terms/update', {
      ...paymentTerm,
      payment_term_id: paymentTermId
    })
  },
  deletePaymentTerm (paymentTermId) {
    return API.httpClient.post('payment-terms/delete', {
      payment_term_id: paymentTermId
    })
  },
  updatePaymentTermOrder (paymentTermIds) {
    return API.httpClient.post('payment-terms/reorder', {
      payment_term_ids: paymentTermIds
    })
  },
  updateOnlineBookingPaymentTerm (paymentTermId) {
    return API.httpClient.post('payment-terms/online-booking', {
      payment_term_id: paymentTermId
    })
  },
  getInvoices ({ requestId, offset, visibleInvoices, sortedColumn, sortedDesc, filterCreatedAt, searchString, filterCustom }) {
    return API.httpClient.get('invoices', {
      params: {
        request_id: requestId,
        offset,
        visible_invoices: visibleInvoices,
        sorted_column: sortedColumn,
        sorted_desc: sortedDesc,
        filter_created_at: filterCreatedAt,
        search_string: searchString,
        filter_custom: filterCustom
      }
    })
  },
  getInvoice (id) {
    return API.httpClient.get(`invoices/${id}`)
  },
  getDraftStatus (id) {
    return API.httpClient.get(`invoices/draft/${id}`)
  },

  //
  // POST
  //
  createInvoice ({ customer, bookingId, displayedPayableAmount, bookingGroupId, paymentTerm, invoiceMessage, orderReference, manualDueDate, manualDepositDueDate, manualDepositAmount, expectedPaymentSchedule, charges = null, customerCreditAmount, accommodationChargesAmount, isCashInvoice, paymentMethod, shouldCheckOut, shouldSendEmail = true, selectedPaymentOption = 'ALL' }) {
    const cart = serializeInvoiceCart(charges)
    const data = {
      customer_id: customer ? customer.id : null,
      displayed_payable_amount: displayedPayableAmount,
      customer_credit_amount: customerCreditAmount,
      customer,
      booking_id: bookingId,
      booking_group_id: bookingGroupId,
      payment_term_id: paymentTerm ? paymentTerm.id : 2,
      manual_due_date: manualDueDate ? manualDueDate.format('YYYY-MM-DD') : null,
      manual_deposit_due_date: manualDepositDueDate ? manualDepositDueDate.format('YYYY-MM-DD') : null,
      manual_deposit_amount: manualDepositAmount,
      invoice_message: invoiceMessage,
      order_reference: orderReference,
      cart,
      accommodation_charges_amount: accommodationChargesAmount,
      is_cash_invoice: isCashInvoice,
      payment_method_id: paymentMethod ? paymentMethod.id : null,
      should_check_out: shouldCheckOut,
      should_send_email: shouldSendEmail,
      selected_payment_option: selectedPaymentOption
    }
    if (expectedPaymentSchedule) {
      data.expected_due_date = expectedPaymentSchedule.dueDate.format('YYYY-MM-DD')
      data.expected_deposit_due_date = expectedPaymentSchedule.depositDueDate ? expectedPaymentSchedule.depositDueDate.format('YYYY-MM-DD') : null
      data.expected_deposit_amount = expectedPaymentSchedule.depositAmount ?? null
    }
    return API.httpClient.post('invoices/create', data)
  },
  createCreditInvoice ({ creditSalesItems, originalInvoiceId }) {
    return API.httpClient.post('invoices/create-credit-invoice', {
      original_invoice_id: originalInvoiceId,
      items: creditSalesItems
    })
  },
  registerInvoicePayment ({ invoiceId, paymentMethodId, accountId, date, amount }) {
    return API.httpClient.post('invoices/register-payment', {
      invoice_id: invoiceId,
      payment_method_id: paymentMethodId,
      account_id: accountId,
      date,
      amount
    })
  },
  checkout ({ invoiceId, paymentMethodId, workstationId, amount, customerCreditAmount }) {
    return API.httpClient.post('invoices/checkout', {
      invoice_id: invoiceId,
      customer_credit_amount: customerCreditAmount,
      payment_method_id: paymentMethodId,
      workstation_id: workstationId,
      amount
    })
  },
  reversePayment ({ paymentId, invoiceId = null }) {
    return API.httpClient.post('invoices/reverse-payment', {
      payment_id: paymentId,
      invoice_id: invoiceId
    })
  },
  reverseInvoicePaymentApplication ({ paymentApplicationId }) {
    return API.httpClient.post('invoices/reverse-payment-application', {
      payment_application_id: paymentApplicationId
    })
  },
  resendInvoice ({ invoiceId, withPaymentLink = false }) {
    return API.httpClient.post('invoices/resend', {
      invoice_id: invoiceId,
      with_payment_link: withPaymentLink
    })
  },
  sendInvoiceReminder ({ invoiceId }) {
    return API.httpClient.post('invoices/send-reminder', {
      invoice_id: invoiceId
    })
  },
  sendPaymentConfirmation ({ paymentId }) {
    return API.httpClient.post('invoices/send-payment-confirmation', {
      payment_id: paymentId
    })
  },
  convertToCredit ({ invoiceId }) {
    return API.httpClient.post('invoices/convert-to-credit', {
      invoice_id: invoiceId
    })
  },
  refundPayment ({ paymentId, invoiceId }) {
    return API.httpClient.post('invoices/refund-payment', {
      payment_id: paymentId,
      invoice_id: invoiceId
    })
  },
  cancelInvoicePayment () {
    return API.httpClient.post('invoices/cancel-payment')
  },
  cancelInvoiceDraftPayment () {
    return API.httpClient.post('invoices/cancel-draft-payment')
  },
  createInvoicingSchedule ({ bookingId, monthsCount, firstInvoiceMonth, selectedPaymentOption }) {
    return API.httpClient.post('invoices/create-invoicing-schedule', {
      booking_id: bookingId,
      installments_count: monthsCount,
      first_month: firstInvoiceMonth.format('YYYY-MM'),
      selected_payment_option: selectedPaymentOption
    })
  },
  cancelInvoicingSchedule ({ bookingId }) {
    return API.httpClient.post('bookings/cancel-invoicing-schedule', {
      booking_id: bookingId
    })
  },
  getVirtualCardRedemptionLink ({ invoiceId }) {
    return API.httpClient.post('invoices/virtual-card-redemption-link', {
      invoice_id: invoiceId
    })
  }
}
